import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { atsFetch, AtsActivity, timeAgo } from './api';
import { inputCls } from './shared';

interface NoteRow extends AtsActivity { entity_name: string | null }

const TYPE_STYLES: Record<string, string> = {
  note: 'bg-blue-50 text-blue-600',
  call: 'bg-emerald-50 text-emerald-600',
  email: 'bg-amber-50 text-amber-600',
  meeting: 'bg-violet-50 text-violet-600',
};

const entityPath = (n: NoteRow) => {
  const base: Record<string, string> = { client: '/ats/clients', person: '/ats/people', role: '/ats/roles' };
  return base[n.entity_type] ? `${base[n.entity_type]}/${n.entity_id}` : null;
};

// Workspace > Notes: everything logged across the ATS in one searchable feed.
// Notes are written on the record they belong to; this is the cross-record view.
const AtsNotes: React.FC = () => {
  const { token } = useAuth();
  const [notes, setNotes] = useState<NoteRow[]>([]);
  const [search, setSearch] = useState('');
  const [type, setType] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    if (!token) return;
    setLoading(true);
    const qs = new URLSearchParams();
    if (search) qs.set('search', search);
    if (type) qs.set('type', type);
    atsFetch(token, `/notes${qs.toString() ? `?${qs}` : ''}`)
      .then((d) => setNotes(d.notes))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token, search, type]);

  useEffect(() => { const t = setTimeout(load, search ? 250 : 0); return () => clearTimeout(t); }, [load, search]);

  return (
    <div className="max-w-4xl">
      <div className="flex items-center gap-3 mb-4">
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search notes…" className={`${inputCls} max-w-xs`} />
        <select value={type} onChange={(e) => setType(e.target.value)} className="border border-gray-200 rounded-lg px-2 py-2 text-sm text-gray-600">
          <option value="">All types</option>
          <option value="note">Notes</option>
          <option value="call">Calls</option>
          <option value="email">Emails</option>
          <option value="meeting">Meetings</option>
        </select>
      </div>

      {loading ? (
        <div className="py-16 text-center text-gray-400 text-sm">Loading…</div>
      ) : notes.length === 0 ? (
        <div className="py-16 text-center">
          <p className="text-sm text-gray-500 mb-1">Nothing logged yet.</p>
          <p className="text-xs text-gray-400">Log notes, calls, emails and meetings on any client, candidate, contact or role — they all collect here.</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {notes.map((n) => {
            const link = entityPath(n);
            return (
              <div key={n.id} className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${TYPE_STYLES[n.type] || TYPE_STYLES.note}`}>{n.type}</span>
                  {n.entity_name && (
                    link
                      ? <Link to={link} className="text-xs font-medium text-gray-700 hover:text-blue-700">{n.entity_name}</Link>
                      : <span className="text-xs font-medium text-gray-700">{n.entity_name}</span>
                  )}
                  <span className="ml-auto text-[11px] text-gray-400">{timeAgo(n.created_at)}</span>
                </div>
                <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">{n.body}</p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AtsNotes;
